import { useState, useContext, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import FormInput from '../components/inputs/FormInput'
import PasswordInput from '../components/inputs/PasswordInput'
import RoundedBorderButtonLink from '../components/buttons/RoundedBorderButtonLink'
import RoundedButton from '../components/buttons/RoundedButton'
import CheckboxInput from '../components/inputs/CheckboxInput'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import login from '../assets/login.png'
import login2 from '../assets/login2.png'
import logo1 from '../assets/logo1.png'
import logo1White from '../assets/logo1-white.png'
import appStore from '../assets/app-store.png'
import googlePlay from '../assets/google-play.png'
import strings from '../res/strings'

const Login = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginErrorMessage, setLoginErrorMessage] = useState('')
  const [isRememberUserChecked, setIsRememberUserChecked] = useState(true)

  const [isLoading, setIsLoading] = useState(false)

  const { loggedUser, setLoggedUser } = useContext(UserContext)
  const navigate = useNavigate()

  useEffect(() => {
    if(loggedUser !== null && loggedUser !== 'refresh') {
      if(loggedUser.role === Constants.Roles.CLIENT) {
        navigate('/schedules')
      } else {
        navigate('/backoffice-schedules')
      }
    } 
  }, [loggedUser])

  // Handle changes in email input text
  const onChangeEmail = (emailText) => {
    setLoginErrorMessage(false)
    setEmail(emailText.target.value)
  }

  // Handle changes in password input text
  const onChangePassword = (passwordText) => {
    setLoginErrorMessage(false)
    setPassword(passwordText.target.value)
  }

  // Check if user is valid and then login to the app
  const loginUser = () => {
    if(email.trim().length === 0 || password.trim().length === 0) {
      setLoginErrorMessage(strings.invalidEmailOrPassword)
    } else {
      setIsLoading(true)

      const userData = {
        email: email.toLowerCase(),
        password: password
      }

      // Login user
      fetch('/api/User/Login', {
        method:'POST',
        headers: { 
          'Accept': 'application/json',
          'Content-Type': 'application/json; charset=UTF-8'
        }, 
        mode: 'cors',
        body: JSON.stringify(userData)
      }).then(async response => {
        setTimeout(async () => {
          setIsLoading(false)

          if (response.status === 200) {
            const jsonRes = await response.json()
  
            setLoggedUser(jsonRes)
  
            localStorage.setItem('authToken', jsonRes.jwtToken)
            
            if(isRememberUserChecked) {
              localStorage.setItem('rememberUser', 'true')
            } else {
              localStorage.setItem('rememberUser', 'false')
            }
  
            if(jsonRes.role === 1) {
              navigate('/schedules')
            } else {
              navigate('/backoffice-schedules')
            }
  
            resetVariables()

          } else if (response.status === 401) {
            setLoginErrorMessage(strings.invalidEmailOrPassword)
            setLoggedUser(null)
            
          } else {
            setLoginErrorMessage(strings.loginError)
            setLoggedUser(null)
          }
        }, 1000)

      }).catch((error) => {
        setTimeout(() => {
          setIsLoading(false)

          setLoginErrorMessage(error)
          setLoggedUser(null)
        }, 1000)
      })
    }
  }

  // Resets local state variables
  const resetVariables = () => {
    setEmail('')
    setPassword('')
    setLoginErrorMessage('')
  }

  return (
    <div className='flex flex-col lg:flex-row'>
      { loggedUser ? null
      : <>
          <div className='hidden lg:flex lg:flex-1'>
            <img className='min-h-screen object-cover' src={login} alt='Login Alagem Digital'/>
          </div>

          <div className='lg:hidden'>
            <img src={login2} alt='Login Alagem Digital'/>
          </div>

          <img className='lg:hidden flex self-center absolute h-[6.938rem] w-[6.938rem] mt-11' src={logo1White} alt='Logo Alagem'/>

          <div className='hidden lg:flex lg:flex-col lg:flex-1 lg:mb-6 lg:items-center lg:justify-center'>
            <img className='h-[12.875rem] w-[12.5rem] mt-6' src={logo1} alt='Logo Alagem'/>
            <p className='font-montserrat text-center mx-[7.5rem] mt-11'>{strings.welcomeMessage}</p>
            <p className='font-montserrat text-center mx-[7.5rem]'>{strings.welcomeMessage2}</p>

            <div className='w-full'>
              <div className='mt-5 text-center h-9 mx-[7.5rem] flex items-center justify-center' style={{ backgroundColor: loginErrorMessage ? 'rgba(232, 95, 92, 0.10)' : null }}>
                <p className='font-montserrat font-medium text-negative'>{loginErrorMessage}</p>
              </div>
            </div>
      
            <div className='flex flex-col w-full mt-5 px-[7.5rem]'>

              <label htmlFor='email' className='font-montserrat font-semibold'>{strings.email}</label>
              <FormInput 
                id='email' 
                type='email'
                value={email}
                onChange={onChangeEmail}
              />

              <label htmlFor='password' className='font-montserrat font-semibold mt-11'>{strings.password}</label>
              <PasswordInput
                value={password}
                onChange={onChangePassword}
              />

              <div className='mt-11 flex flex-row items-center justify-between'>
                <div className='flex items-center'>
                  <CheckboxInput
                    id='checkbox'
                    checked={isRememberUserChecked}
                    onChange={() => setIsRememberUserChecked(!isRememberUserChecked)}
                    disableRipple
                  />
                  <label htmlFor='checkbox' className='font-montserrat cursor-pointer'>{strings.rememberUser}</label>
                </div>
                <Link to='/recover-password' className='font-montserrat underline text-accent'>{strings.recoverPassword}</Link>
              </div>

              <div className='mt-12 flex flex-row space-x-6'>
                <RoundedBorderButtonLink 
                  path='/create-account'
                  text={strings.createAccount}
                />

                <RoundedButton 
                  onClick={loginUser}
                  text={strings.login}
                  height={48}
                  bgColor='#007EB4'
                />
              </div>

            </div>     
          </div>

          <div className='lg:hidden absolute bottom-0 w-full rounded-t-2xl bg-white px-4 py-9'>
            <p className='font-montserrat text-center'>{strings.mobile}</p>

            <button className='mt-11' onClick={() => console.log('Open app in Google Play Store')}>
              <img src={googlePlay} alt='Google Play Store'/>
            </button>

            <button onClick={() => console.log('Open app in Apple App Store')}>
              <img src={appStore} alt='Apple App Store'/>
            </button>
          </div>

          { isLoading ?
            <LoadingModal />
          : null
          }
          
        </>
      }
    </div>
  )
}

export default Login